/**
 * Represents an object that could be not loaded, loaded or loading
 */
export type Loadable<T> = Readonly<(LoadedData<T> | NotLoadedData) & LoadingData & { isValid: boolean }>;

type UnwrapLoadable<T> = T extends LoadedData<infer U> ? U : never;
export type UnwrapLoadableArray<X, T extends Loadable<X>[]> = {
  [K in keyof T]: UnwrapLoadable<T[K]>;
};

export const isLoadableEmpty = <T>(l: Loadable<T>): boolean => !l.loading && !l.loaded;

/**
 * Contains methods for working with loadable objects
 */
export const loadable = {
  empty<T>(): Loadable<T> {
    return {
      loaded: false,
      loading: false,
      isValid: false,
    };
  },

  fill<T>(payload: T) {
    return {
      loaded: false,
      loading: false,
      isValid: false,
      data: payload,
    };
  },

  loading(payload = null) {
    return {
      loaded: false,
      loading: true,
      isValid: false,
      data: payload,
    };
  },

  loaded<T>(payload: T): Loadable<T> {
    return {
      loaded: true,
      loading: false,
      isValid: true,
      data: payload,
    };
  },
};

interface LoadingData {
  loading: boolean;
}

interface NotLoadedData {
  loaded: false;
}

interface LoadedData<T> {
  loaded: true;
  data: T;
}

declare module '@vue/reactivity' {
  export interface RefUnwrapBailTypes {
    dateTypes: Date | Loadable<unknown>;
  }
}

export type ValueOrDefault<T, TDefault> = TDefault extends T ? T : T | TDefault;
